import { Link } from "react-router-dom";
import { broker, office } from "@/config/broker.config";
import { siteConfig } from "@/config/website.config";
import { FiPhone, FiMail, FiMapPin, FiClock, FiExternalLink } from "@/lib/icons";
import { ContactForm } from "./_components/ContactForm";

export function ContactPage() {
  return (
    <>
      {/* Hero Section with Background Image */}
      <section className="relative bg-cream overflow-hidden">
        {/* Background - uses hero image with overlay, can be replaced with /images/contact-bg.jpg */}
        <div
          className="absolute inset-0 opacity-[0.08]"
          style={{
            backgroundImage: `url('/hero-image.jpg')`,
            backgroundSize: 'cover',
            backgroundPosition: 'center right',
          }}
        />
        {/* Decorative gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-r from-cream via-cream/80 to-transparent" />
        <div className="relative max-w-6xl mx-auto px-4 md:px-6 py-16 md:py-24">
          <div className="max-w-2xl">
            <p className="section-label mb-4">Contact Us</p>
            <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-tight mb-6">
              Let's start your
              <br />
              <span className="italic">home loan journey</span>
            </h1>
            <p className="text-lg text-muted-foreground">
              Whether you're buying your first home, refinancing, or investing,
              we're here to help you find the right loan. Get in touch for a free,
              no-obligation consultation.
            </p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16 md:py-24">
        <div className="max-w-6xl mx-auto px-4 md:px-6">
          <div className="grid lg:grid-cols-5 gap-12 lg:gap-16">
            {/* Contact Form */}
            <div className="lg:col-span-3">
              <div className="bg-white rounded-3xl border border-border p-6 md:p-8 shadow-sm">
                <h2 className="font-serif text-2xl md:text-3xl mb-2">
                  Send us a message
                </h2>
                <p className="text-muted-foreground mb-8">
                  Fill out the form below and we'll get back to you within 24-48 business hours.
                </p>
                <ContactForm />
              </div>
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-2 space-y-8">
              {/* Broker Card */}
              <div className="bg-primary rounded-3xl p-6 text-primary-foreground">
                <div className="flex items-center gap-4 mb-6">
                  <img
                    src={broker.image}
                    alt={broker.name}
                    className="w-16 h-16 rounded-full object-cover"
                  />
                  <div>
                    <h3 className="font-serif text-xl">{broker.name}</h3>
                    <p className="text-primary-foreground/90 text-sm">
                      {broker.role}
                    </p>
                  </div>
                </div>
                <p className="text-primary-foreground/80 text-sm mb-6">
                  {broker.shortBio}
                </p>
                <div className="space-y-3">
                  <a
                    href={`tel:${broker.phone.replace(/\s/g, "")}`}
                    className="flex items-center gap-3 text-sm hover:text-accent transition-colors"
                  >
                    <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center">
                      <FiPhone className="size-4" />
                    </div>
                    {broker.phone}
                  </a>
                  <a
                    href={`mailto:${broker.email}`}
                    className="flex items-center gap-3 text-sm hover:text-accent transition-colors"
                  >
                    <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center">
                      <FiMail className="size-4" />
                    </div>
                    {broker.email}
                  </a>
                  {/* LinkedIn - commented out for now
                  {broker.socials?.linkedin && (
                    <a
                      href={broker.socials.linkedin}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-3 text-sm hover:text-accent transition-colors"
                    >
                      <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center">
                        <FiLinkedin className="size-4" />
                      </div>
                      LinkedIn
                    </a>
                  )}
                  */}
                </div>
              </div>

              {/* Quick Info Cards */}
              <div className="bg-muted rounded-3xl p-6">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-full bg-accent flex items-center justify-center shrink-0">
                    <FiClock className="size-4 text-accent-foreground" />
                  </div>
                  <div>
                    <h4 className="font-medium mb-1">Response Time</h4>
                    <p className="text-sm text-muted-foreground">
                      We typically respond within 24-48 business hours.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-muted rounded-3xl p-6">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-full bg-accent flex items-center justify-center shrink-0">
                    <FiMapPin className="size-4 text-accent-foreground" />
                  </div>
                  <div>
                    <h4 className="font-medium mb-1">Service Area</h4>
                    <p className="text-sm text-muted-foreground">
                      Based in Springfield, we help clients across Brisbane, Logan,
                      Ipswich, and Australia-wide via video consultations.
                    </p>
                  </div>
                </div>
              </div>

              {/* Our Office */}
              <div className="bg-muted rounded-3xl p-6">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-full bg-accent flex items-center justify-center shrink-0">
                    <FiMapPin className="size-4 text-accent-foreground" />
                  </div>
                  <div>
                    <h4 className="font-medium mb-1">Our Office</h4>
                    <p className="text-sm text-muted-foreground mb-2">
                      {office.line1}
                      <br />
                      {office.suburb}, {office.state} {office.postcode}
                    </p>
                    <a
                      href={office.mapUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 text-sm text-primary hover:underline"
                    >
                      View on map
                      <FiExternalLink className="size-3" />
                    </a>
                  </div>
                </div>
              </div>

              {/* Accreditation */}
              <div className="text-center pt-4">
                <p className="text-xs text-muted-foreground">
                  {broker.name} is an accredited broker operating under
                  <br />
                  {siteConfig.business.aclProvider} Australian Credit Licence{" "}
                  {siteConfig.business.acl}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="bg-cream py-16 md:py-24">
        <div className="max-w-4xl mx-auto px-4 md:px-6">
          <div className="text-center mb-12">
            <p className="section-label mb-4">Common Questions</p>
            <h2 className="font-serif text-3xl md:text-4xl">
              Before you reach out
            </h2>
          </div>

          <div className="space-y-6">
            {[
              {
                q: "Is your service really free?",
                a: "Yes! Our service is completely free to you. We're paid by the lender when your loan settles, so there's no cost or obligation on your end.",
              },
              {
                q: "How long does the loan process take?",
                a: "Typically 4-6 weeks from application to settlement, though this can vary. We'll keep you informed every step of the way.",
              },
              {
                q: "What documents do I need to apply?",
                a: "Generally you'll need ID, payslips, bank statements, and details of any existing debts. We'll provide a complete checklist based on your situation.",
              },
              {
                q: "Can you help if I'm self-employed?",
                a: "Absolutely! We have extensive experience with self-employed borrowers and know which lenders are most flexible with business income.",
              },
            ].map((item) => (
              <div
                key={item.q}
                className="bg-white rounded-2xl p-6 shadow-sm"
              >
                <h3 className="font-medium text-lg mb-2">{item.q}</h3>
                <p className="text-muted-foreground">{item.a}</p>
              </div>
            ))}
          </div>

          <p className="text-center mt-8 text-muted-foreground">
            Have more questions?{" "}
            <Link to="/guides" className="text-foreground underline hover:text-accent">
              Check our guides
            </Link>{" "}
            or send us a message above.
          </p>
        </div>
      </section>
    </>
  );
}
